// src/components/pages/admin-dash/AdminScratchSources.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";
import AdminTable from "./AdminTable";
import ConfirmDialog from "../../ui/ConfirmDialog";

/**
 * Admin view for the Scratch-Off Intelligence module.
 *
 * Two responsibilities:
 *   1. List the configured per-state data sources (rows in
 *      scratch_state_source) and toggle each one enabled/disabled.
 *   2. Trigger manual ingestion runs — one specific state, or all of
 *      them. Each run hits the backend's POST /admin/scratch/ingest/*
 *      endpoint, which the daily Quarkus scheduler also calls.
 *
 * Backend endpoints used (all under /admin/scratch):
 *   GET    /sources
 *   POST   /sources/{id}/enable
 *   POST   /sources/{id}/disable
 *   POST   /ingest/all
 *   POST   /ingest/{state}
 *   POST   /cache/evict
 *
 * Props:
 *   mode          "sources" | "ingest"   which sub-view to show
 *   onShowToast   fn(message, tone)
 */
const SOURCE_TYPE_TONES = {
	API:  { bg: "var(--teal-dim)",   fg: "var(--teal)",   border: "var(--teal-border)"   },
	CSV:  { bg: "var(--gold-dim)",   fg: "var(--gold)",   border: "var(--gold-border)"   },
	HTML: { bg: "var(--ink-dim)",    fg: "var(--ink)",    border: "var(--ink-border)"    },
};

function relTime(iso) {
	if (!iso) return "—";
	const then = new Date(iso).getTime();
	if (!Number.isFinite(then)) return "—";
	const diff = Math.max(0, Date.now() - then);
	const m = Math.floor(diff / 60000);
	if (m < 1) return "just now";
	if (m < 60) return `${m}m ago`;
	const h = Math.floor(m / 60);
	if (h < 24) return `${h}h ago`;
	const d = Math.floor(h / 24);
	if (d < 30) return `${d}d ago`;
	return new Date(iso).toLocaleDateString();
}

export default function AdminScratchSources({ mode = "sources", onShowToast }) {
	const [sources, setSources]     = useState([]);
	const [loading, setLoading]     = useState(true);
	const [busyId, setBusyId]       = useState(null);    // id of source being toggled
	const [runningState, setRunning] = useState(null);   // state code being ingested
	const [runningAll, setRunningAll] = useState(false);
	const [lastResults, setLastResults] = useState([]);  // most recent ingestion results
	const [confirm, setConfirm]     = useState(null);    // { source, nextEnabled } | null

	const fetchSources = useCallback(async () => {
		setLoading(true);
		try {
			const res = await axiosInstance.get("/admin/scratch/sources");
			const data = Array.isArray(res.data) ? res.data : [];
			data.sort((a, b) => String(a.stateCode || "").localeCompare(String(b.stateCode || "")));
			setSources(data);
		} catch {
			setSources([]);
			onShowToast?.("Couldn't load scratch sources.", "error");
		} finally {
			setLoading(false);
		}
	}, [onShowToast]);

	useEffect(() => { fetchSources(); }, [fetchSources]);

	/* ------------------------------------------------------------------ */
	/* Enable / disable                                                   */
	/* ------------------------------------------------------------------ */

	const toggleSource = async (source) => {
		const nextEnabled = !source.enabled;
		setBusyId(source.id);
		try {
			const path = nextEnabled ? "enable" : "disable";
			await axiosInstance.post(`/admin/scratch/sources/${source.id}/${path}`);
			setSources((prev) =>
				prev.map((s) => (s.id === source.id ? { ...s, enabled: nextEnabled } : s)));
			onShowToast?.(
				`${source.stateCode} source ${nextEnabled ? "enabled" : "disabled"}.`,
				"success",
			);
		} catch {
			onShowToast?.("Couldn't update source.", "error");
		} finally {
			setBusyId(null);
			setConfirm(null);
		}
	};

	/* ------------------------------------------------------------------ */
	/* Manual ingest                                                      */
	/* ------------------------------------------------------------------ */

	const runOne = async (stateCode) => {
		setRunning(stateCode);
		setLastResults([]);
		try {
			const res = await axiosInstance.post(`/admin/scratch/ingest/${stateCode}`);
			const result = res?.data || { stateCode, success: false, message: "no response" };
			setLastResults([result]);
			if (result.success) {
				onShowToast?.(
					`${stateCode}: ${result.gamesUpserted ?? 0} games, ${result.alertsRaised ?? 0} alerts.`,
					"success",
				);
			} else {
				onShowToast?.(`${stateCode}: ${result.message || "failed"}.`, "error");
			}
		} catch {
			onShowToast?.(`Couldn't run ingestion for ${stateCode}.`, "error");
		} finally {
			setRunning(null);
		}
	};

	const runAll = async () => {
		setRunningAll(true);
		setLastResults([]);
		try {
			const res = await axiosInstance.post("/admin/scratch/ingest/all");
			const results = Array.isArray(res.data) ? res.data : [];
			setLastResults(results);
			const ok = results.filter((r) => r.success).length;
			onShowToast?.(`${ok}/${results.length} states ingested successfully.`,
				ok === results.length ? "success" : "error");
		} catch {
			onShowToast?.("Couldn't run ingestion.", "error");
		} finally {
			setRunningAll(false);
		}
	};

	const evictCache = async () => {
		try {
			await axiosInstance.post("/admin/scratch/cache/evict");
			onShowToast?.("Scratch cache evicted.", "success");
		} catch {
			onShowToast?.("Couldn't evict cache.", "error");
		}
	};

	/* ------------------------------------------------------------------ */
	/* Sources table                                                      */
	/* ------------------------------------------------------------------ */

	const columns = useMemo(() => [
		{
			key: "stateCode",
			label: "State",
			sort: true,
			width: "90px",
			render: (r) => (
				<div className="font-semibold tracking-wider" style={{ color: "var(--txt)" }}>
					{r.stateCode}
				</div>
			),
		},
		{
			key: "stateName",
			label: "Name",
			sort: true,
		},
		{
			key: "sourceType",
			label: "Type",
			sort: true,
			width: "100px",
			render: (r) => {
				const tone = SOURCE_TYPE_TONES[r.sourceType] || SOURCE_TYPE_TONES.HTML;
				return (
					<span
						className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold"
						style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
					>
						{r.sourceType}
					</span>
				);
			},
		},
		{
			key: "sourceUrl",
			label: "URL",
			render: (r) => (
				<a
					href={r.sourceUrl}
					target="_blank"
					rel="noreferrer noopener"
					className="text-[12px] underline truncate inline-block max-w-[360px]"
					style={{ color: "var(--teal)" }}
					title={r.sourceUrl}
				>
					{r.sourceUrl}
				</a>
			),
		},
		{
			key: "updatedAt",
			label: "Updated",
			sort: true,
			width: "100px",
			value: (r) => r.updatedAt ? new Date(r.updatedAt).getTime() : 0,
			render: (r) => (
				<span className="text-[12px]" style={{ color: "var(--txt-2)" }}>
					{relTime(r.updatedAt)}
				</span>
			),
		},
		{
			key: "health",
			label: "Ingestion health",
			sort: true,
			width: "160px",
			value: (r) => r.lastRunAt ? new Date(r.lastRunAt).getTime() : -1,
			render: (r) => {
				if (!r.lastRunAt) {
					return <span className="text-[12px]" style={{ color: "var(--txt-3)" }}>Never run</span>;
				}
				const failing = !!r.lastError;
				return (
					<div title={r.lastError || ""}>
						<Badge variant={failing ? "red" : "teal"}>
							{failing ? "Failing" : "OK"}
						</Badge>
						<div className="text-[11px] mt-1" style={{ color: "var(--txt-3)" }}>
							last run {relTime(r.lastRunAt)}
							{failing && r.lastSuccessAt && ` · last OK ${relTime(r.lastSuccessAt)}`}
						</div>
					</div>
				);
			},
		},
		{
			key: "enabled",
			label: "Status",
			sort: true,
			width: "100px",
			align: "center",
			render: (r) => (
				<Badge variant={r.enabled ? "teal" : "neutral"}>
					{r.enabled ? "Enabled" : "Disabled"}
				</Badge>
			),
		},
		{
			key: "actions",
			label: "",
			align: "right",
			width: "200px",
			render: (r) => (
				<div className="flex items-center justify-end gap-2">
					<Button
						size="sm"
						variant="outline"
						loading={runningState === r.stateCode}
						disabled={!r.enabled || runningAll || runningState === r.stateCode}
						onClick={(e) => { e.stopPropagation(); runOne(r.stateCode); }}
					>
						<Icon name="refresh" size={12} />
						Ingest
					</Button>
					<Button
						size="sm"
						variant={r.enabled ? "ghost" : "outline"}
						loading={busyId === r.id}
						disabled={busyId === r.id}
						onClick={(e) => {
							e.stopPropagation();
							setConfirm({ source: r, nextEnabled: !r.enabled });
						}}
					>
						{r.enabled ? "Disable" : "Enable"}
					</Button>
				</div>
			),
		},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	], [busyId, runningState, runningAll]);

	/* ------------------------------------------------------------------ */
	/* Render                                                             */
	/* ------------------------------------------------------------------ */

	if (mode === "ingest") {
		return (
			<div className="space-y-6">
				<header>
					<h1 className="font-semibold" style={{ fontSize: 24, color: "var(--txt)" }}>
						Scratch-Offs · Manual ingest
					</h1>
					<p className="mt-1 text-[13px]" style={{ color: "var(--txt-2)" }}>
						Run an ad-hoc data refresh outside the daily 06:30 ET scheduler.
						Each enabled state hits its public source, persists games + prize tiers,
						re-scores the cohort, and raises any new alerts.
					</p>
				</header>

				<Card>
					<div className="flex flex-wrap items-center gap-3">
						<Button
							size="md"
							onClick={runAll}
							loading={runningAll}
							disabled={runningAll || runningState !== null}
						>
							<Icon name="refresh" size={14} />
							Run all enabled states
						</Button>
						<Button
							size="md"
							variant="outline"
							onClick={evictCache}
							disabled={runningAll || runningState !== null}
						>
							<Icon name="trash" size={14} />
							Evict cache only
						</Button>
						<div className="text-[12px] ml-auto" style={{ color: "var(--txt-3)" }}>
							{loading ? "Loading sources…" : `${sources.filter((s) => s.enabled).length} enabled`}
						</div>
					</div>
				</Card>

				{!loading && sources.length > 0 && (
					<Card variant="soft">
						<div className="lg-label mb-3">Per-state run</div>
						<div className="flex flex-wrap gap-2">
							{sources.filter((s) => s.enabled).map((s) => (
								<Button
									key={s.id}
									size="sm"
									variant="outline"
									loading={runningState === s.stateCode}
									disabled={runningAll || runningState !== null}
									onClick={() => runOne(s.stateCode)}
								>
									<Icon name="refresh" size={12} />
									{s.stateCode}
								</Button>
							))}
						</div>
					</Card>
				)}

				{lastResults.length > 0 && (
					<Card>
						<div className="lg-label mb-3">Last run</div>
						<div className="space-y-2">
							{lastResults.map((r, i) => (
								<div
									key={i}
									className="flex items-center gap-3 px-3 py-2 rounded-lg"
									style={{
										background: "var(--surf-2)",
										border: "1px solid var(--border)",
									}}
								>
									<Icon
										name={r.success ? "check-circle" : "alert-triangle"}
										size={16}
										style={{ color: r.success ? "var(--teal)" : "var(--red)" }}
									/>
									<span className="font-semibold" style={{ color: "var(--txt)" }}>
										{r.stateCode}
									</span>
									{r.success ? (
										<span className="text-[12px]" style={{ color: "var(--txt-2)" }}>
											{r.gamesUpserted} games · {r.tiersUpserted} tiers · {r.alertsRaised} alerts
										</span>
									) : (
										<span className="text-[12px]" style={{ color: "var(--red)" }}>
											{r.message || "failed"}
										</span>
									)}
								</div>
							))}
						</div>
					</Card>
				)}
			</div>
		);
	}

	// mode === "sources" (default)
	return (
		<div className="space-y-6">
			<header className="flex items-end justify-between flex-wrap gap-3">
				<div>
					<h1 className="font-semibold" style={{ fontSize: 24, color: "var(--txt)" }}>
						Scratch-Offs · Sources
					</h1>
					<p className="mt-1 text-[13px]" style={{ color: "var(--txt-2)" }}>
						Per-state data sources for the scratch-off intelligence pipeline.
						Toggle a source to take it in or out of the daily ingestion run.
					</p>
				</div>
				<Button
					size="sm"
					variant="outline"
					onClick={fetchSources}
					loading={loading}
					icon="refresh"
				>
					Refresh
				</Button>
			</header>

			{loading ? (
				<div className="flex items-center justify-center py-16" style={{ color: "var(--txt-2)" }}>
					<Spinner size={20} />
					<span className="ml-2 text-[13px]">Loading sources…</span>
				</div>
			) : (
				<AdminTable
					columns={columns}
					rows={sources}
					rowKey="id"
					emptyMessage="No scratch-off data sources configured yet."
					emptyIcon="database"
					defaultSort={{ key: "stateCode", dir: "asc" }}
				/>
			)}

			{confirm && (
				<ConfirmDialog
					open={!!confirm}
					title={confirm.nextEnabled ? "Enable source?" : "Disable source?"}
					message={
						confirm.nextEnabled
							? `${confirm.source.stateCode} (${confirm.source.sourceType}) will be included in the next daily ingestion run.`
							: `${confirm.source.stateCode} (${confirm.source.sourceType}) will be skipped on the next daily ingestion run. Existing games stay in place — just no new updates.`
					}
					confirmLabel={confirm.nextEnabled ? "Enable" : "Disable"}
					tone={confirm.nextEnabled ? "default" : "danger"}
					loading={busyId === confirm.source.id}
					onConfirm={() => toggleSource(confirm.source)}
					onClose={() => setConfirm(null)}
				/>
			)}
		</div>
	);
}
